import {
  atom,
  onConnect,
  onDisconnect,
  onUpdate,
  reatomAsync,
  reatomResource,
  withDataAtom,
  withErrorAtom,
  withInit,
  withReset,
  withStatusesAtom,
} from '@reatom/framework';
import {
  StravaAthleteSchema,
  type StravaDetailedPhoto,
  type StravaAthleteType,
} from './strava-types';
import jsCookies from 'js-cookie';
import type { DetailedActivity, SummaryActivity } from 'strava';
import {
  STRAVA_ACCESS_TOKEN_COOKIE_NAME,
  STRAVA_ATHLETE_COOKIE_NAME,
} from './strava-constants';
import { safeParse } from 'valibot';
import { getStravaClient } from './strava-api';
import posthog from 'posthog-js';

export const stravaAthleteAtom = atom<StravaAthleteType | null>(
  null,
  'stravaAthleteAtom',
).pipe(
  withInit(() => {
    const athlete = jsCookies.get(STRAVA_ATHLETE_COOKIE_NAME);
    if (!athlete) return null;

    const athleteParsed = safeParse(StravaAthleteSchema, JSON.parse(athlete));
    if (!athleteParsed.success) {
      jsCookies.remove(STRAVA_ATHLETE_COOKIE_NAME);
      return null;
    }

    posthogIdentify(athleteParsed.output);

    return athleteParsed.output;
  }),
);

export const stravaAthleteFullNameAtom = atom((ctx) => {
  const athlete = ctx.spy(stravaAthleteAtom);
  if (!athlete) return null;

  return [athlete.firstName, athlete.lastName].filter(Boolean).join(' ');
}, 'stravaAthleteFullNameAtom');

const posthogIdentify = (athlete: StravaAthleteType | null) => {
  if (!athlete || typeof window === 'undefined') return;

  posthog.identify(`strava_${athlete.id.toString()}`, {
    name: [athlete.firstName, athlete.lastName].filter(Boolean).join(' '),
  });
};

onUpdate(stravaAthleteAtom, (_ctx, athlete) => {
  posthogIdentify(athlete);
});

export const stravaAccessTokenAtom = atom<string | null>(
  null,
  'stravaAccessTokenAtom',
).pipe(
  withInit(() => {
    return jsCookies.get(STRAVA_ACCESS_TOKEN_COOKIE_NAME) ?? null;
  }),
);

export const fetchStravaActivitiesAtom = reatomResource(async (ctx) => {
  const accessToken = ctx.spy(stravaAccessTokenAtom);
  if (!accessToken) {
    return [];
  }

  return await ctx.schedule(async () => {
    const stravaClient = getStravaClient(ctx);

    const activities = await stravaClient<SummaryActivity[]>({
      method: 'GET',
      path: '/athlete/activities',
      accessToken,
    });

    return activities;
  });
}, 'fetchStravaActivitiesAtom').pipe(
  withDataAtom([]),
  withStatusesAtom(),
  withErrorAtom(),
);

onConnect(fetchStravaActivitiesAtom.dataAtom, (ctx) => {
  fetchStravaActivitiesAtom(ctx);
});

export const stravaActivitiesPendingAtom = atom(
  (ctx) => ctx.spy(fetchStravaActivitiesAtom.statusesAtom).isPending,
  'stravaActivitiesPendingAtom',
);

export const filteredStravaActivitiesAtom = atom((ctx) => {
  const data = ctx.spy(fetchStravaActivitiesAtom.dataAtom);

  return data.filter((activity) => {
    return activity.type === 'Run' || activity.type === 'Ride';
  });
}, 'filteredStravaActivitiesAtom');

export const fetchStravaActivityByIdAtom = reatomAsync(
  async (ctx, activityId: number | string) => {
    const accessToken = ctx.get(stravaAccessTokenAtom);
    if (!accessToken) {
      return null;
    }

    const stravaClient = getStravaClient(ctx);

    const activity = await stravaClient<DetailedActivity>({
      method: 'GET',
      path: `/activities/${activityId}`,
      accessToken,
    });

    let photos: StravaDetailedPhoto[] = [];

    // Load Photos if there are any
    if (activity.photos.count > 0) {
      photos = await stravaClient<StravaDetailedPhoto[]>({
        method: 'GET',
        path: `/activities/${activityId}/photos?size=2048&photo_sources=1`,
        accessToken,
      });
    }
    return { activity, photos };
  },
  'fetchStravaActivityByIdAtom',
).pipe(withDataAtom(null), withStatusesAtom(), withErrorAtom(), withReset());

onDisconnect(fetchStravaActivityByIdAtom.dataAtom, (ctx) => {
  fetchStravaActivityByIdAtom.dataAtom(ctx, null);
});

export const fetchStravaActivityByIdPendingAtom = atom(
  (ctx) => ctx.spy(fetchStravaActivityByIdAtom.statusesAtom).isPending,
  'fetchStravaActivityByIdPendingAtom',
);
